import { useQueryClient } from "react-query";
import PrivateApi from "../../services/PrivateApiService"
import { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, FormCheck, ListGroup, ListGroupItem, ProgressBar, Row } from "react-bootstrap";
import DownloadInvoiceListItem from "./DownloadInvoiceListItem";
import { FaCloudDownloadAlt, FaFilePdf } from "react-icons/fa";
import { FaFileCircleXmark, FaFileZipper } from "react-icons/fa6";

/**
 * DownloadInvoiceList Component
 * 
 * This component allows users to view and download a list of invoices.
 * It manages UI state related to invoice selection, download progress, and errors.
 * 
 * Props:
 * @param {array} invoices The list of invoice objects to display and manage
 * @param {boolean} hideQuickAccess A flag to optionally hide quick access UI elements
 * 
 * @returns UI list
 */
const DownloadInvoiceList = ({invoices, hideQuickAccess}) => {

    const API = new PrivateApi(); // API object
    const queryClient = useQueryClient(); // query client to refresh list after download
    const [view, setView] = useState("list"); // current view on ui list
    const [downloadList, setDownloadList] = useState([]); // array of files to downlaod
    const [downloadBtnHidden, setDownloadBtnHidden] = useState(true); // show/hide download button
    const [countSelected, setCountSelected] = useState(0); // number of selected items in download list

    const [downloadError, setDownloadError] = useState(""); // download error message
    const [percentDone, setPercentDone] = useState(0); // percentage of prepared files in be
    const [uniqueFolder, setUniqueFolder] = useState(""); // name of unique folder where prepared files are located
    const [downloadFiles, setDownloadFiles] = useState(null); // array of zips at to download


    //! ----------/
    //! Effects  /
    //!---------/
    // effect for changing view on list
    useEffect(() => {
        let interval; // set interval

        //* if in preparing view
        if (view === "preparing") {
            hideQuickAccess(true); // hide quick access cards

            // do in interval
            interval = setInterval(() => {
                // prepare data to send to BE
                var data = {max: downloadList.length, folder: uniqueFolder, percent: percentDone};

                // send check to BE
                API.post_Invoices_prepare_check(data)
                    .then(res => {

                        // if response percent was same as before
                        // fake change
                        if (Number(res.data.percent) === percentDone) {
                            setPercentDone(prev => prev + 0.5);

                        // else set percentage from BE
                        } else {
                            setPercentDone(Number(res.data.percent))
                        }

                        // if percent is 100 change to zip view and clear interval
                        if (Number(res.data.percent) === 100 && res.data.is_done !== undefined && res.data.is_done === true) {
                            setView("zip");
                            clearInterval(interval);
                        }
                    })
                    .catch(err => {
                        // error can only occur if files aren't already present
                        if (downloadFiles === null) {
                            setView("error");
                            if (err.response.data.detail !== undefined) {
                                setDownloadError(err.response.data.detail);
                            } else {
                                setDownloadError("Bitte versuchen Sie es später noch einmal");
                            } 
                        }
                    })
            }, 4000); // interval is 4sec.
        }

        //* if in zip view
        if (view === "zip") {
            // send call to BE to zip files in folder
            API.post_Invoices_prepare_zip({folder: uniqueFolder})
                .then(res => {
                    setDownloadFiles(res.data);
                    setView("download");
                })
                .catch(err => {
                    setView("error");
                    if (err.response.data.detail !== undefined) {
                        setDownloadError(err.response.data.detail);
                    } else {
                        setDownloadError("Bitte versuchen Sie es später noch einmal");
                    } 
                });
        }

        //* if in download view
        if (view === "download") {
            // invalidate all invoice query
            queryClient.invalidateQueries("GET_ALL_INVOICES");
        }
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view])


    //! -----------/
    //! Functions /
    //! ---------/
    // check/uncheck all 
    const toggleAllCheckboxes = (checked) => {        
        var checkboxes = document.getElementsByClassName('form-check-input');
        var tempList = [];
        if (checked) {
            for (let i=0; i < checkboxes.length; i++) {
                checkboxes[i].checked = true;
                if (checkboxes[i].id !== "none:0") {                 
                    tempList.push({"type": checkboxes[i].id.split(":")[0], "id": checkboxes[i].id.split(":")[1]});
                }
            }
            setCountSelected(tempList.length);
            setDownloadBtnHidden(false);
        } else {
            for (let i=0; i < checkboxes.length; i++) {
                checkboxes[i].checked = false;
            }
            setDownloadBtnHidden(true);
        }
        setDownloadList(tempList)
    }

    // single check/uncheck
    const toggleSingleCheckbox = (e, type, id) => {        
        if (e.target.checked) {
            downloadList.push({type: type, id: id})
            setCountSelected(downloadList.length)
            setDownloadBtnHidden(false);
        } else {
            var tmpList = downloadList.filter(dl => Number(dl.id) !== Number(id));
            if (tmpList.length === 0) {
                setDownloadBtnHidden(true);
            }
            setDownloadList([...tmpList]);
            setCountSelected(prev => prev = tmpList.length);
        }
    }


    const downloadBtnHandler = () => {
        if (downloadList.length > 0) {
            API.post_Invoices_prepare({download_list: downloadList})
            .then((res) => {
                    setUniqueFolder(res.data.folder);
                    setView("preparing");
                })
                .catch(err => {
                    setView("error");
                    if (err.response.data.detail !== undefined) {
                        setDownloadError(err.response.data.detail);
                    } else {
                        setDownloadError("Bitte versuchen Sie es später noch einmal");
                    } 
                });
        }
    }


    const downloadFileHandler = (file, fileName, e) => {
        e.preventDefault();
        let byteCharacters = atob(file);
        let byteNumbers = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        let blob = new Blob([byteNumbers], { type: "application/zip" });

        // Create a downloadable link
        let a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fileName;  // File name
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }


    //! GUI
    return (
        <Card style={{borderRadius: 15}}>
            {
                view === "list"?
                    <>
                        <Row className="mt-4" style={{marginLeft: 5, marginRight: 5}}>
                            <Col xl="1" key="allCheckbox" xs="10" md="1">
                                <FormCheck 
                                    id="none:0"
                                    type="checkbox" 
                                    onChange={e => toggleAllCheckboxes(e.target.checked)}
                                />
                            </Col>
                            <Col xl="1">Von</Col>
                            <Col xs="12" xl="3">Rechnungsart</Col>
                            <Col xl="3">Objekt</Col>
                            <Col xs="6" xl="2">Datum</Col>
                            <Col xl="2" className="text-center">
                                {downloadBtnHidden?
                                    <span>Downloaded</span>
                                    :
                                    <Button onClick={() => downloadBtnHandler()} className="btn-energo">Download ({countSelected})</Button>
                                }
                            </Col>
                        </Row>
                        <ListGroup className="mt-3">
                            {invoices.length > 0 ?
                                invoices.map(inv => (
                                    <DownloadInvoiceListItem
                                        key={
                                            inv.x_file_poolrechnung_name !== undefined? `poolrechnung:${inv.id}`:
                                            inv.x_file_abrechnung_name !== undefined ? `abrechnung:${inv.id}`:
                                            inv.x_file_vorschreibung_name !== undefined? `vorschreibung:${inv.id}`:
                                            null
                                        }
                                        invoice={inv}
                                        singleCheckHandler={toggleSingleCheckbox}
                                        type={
                                            inv.x_file_poolrechnung_name !== undefined? "poolrechnung":
                                            inv.x_file_abrechnung_name !== undefined? "abrechnung":
                                            inv.x_file_vorschreibung_name !== undefined? "vorschreibung":
                                            ""
                                        }
                                    />
                                ))
                            :
                                <ListGroupItem key="none:0" className="text-center">
                                    <small className="text-muted">Keine Dokumente</small>
                                </ListGroupItem>
                            }
                        </ListGroup>
                    </>:

                view === "preparing"?
                    <Card.Body className="text-center">
                        <Row className="mt-3">
                            <Col>
                                <FaFilePdf 
                                    size={60} 
                                    className="text-danger animate__animated animate__bounce animate__infinite" 
                                />
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <h4>Ihre Dateien</h4>
                                <p className="text-muted">werden für Sie vorbereitet</p>
                            </Col>
                        </Row>
                        <Row className="justify-content-center mt-3 mb-3">
                            <Col xs="4">
                                <ProgressBar 
                                    variant={"danger"}
                                    animated 
                                    now={percentDone} 
                                />
                            </Col>
                        </Row>
                    </Card.Body>:

                view === "zip"?
                    <CardBody className="text-center">
                        <Row className="mt-3">
                            <Col>
                                <FaFilePdf className="text-danger animate__animated animate__infinite animate__slow	 animate__fadeOutRight" size={60} />
                                <FaFileZipper style={{color: "#f5c542"}} size={60} />
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <h4>Ihre Dateien</h4>
                                <p className="text-muted">werden komprimiert</p>
                            </Col>
                        </Row>
                    </CardBody>:

                view === "download"?
                    <CardBody className="text-center">
                        <Row className="mt-3">
                            <Col className="text-end">
                                <Button 
                                    variant="warning" 
                                    className="btn-sm"
                                    onClick={() => setView("list")}
                                >
                                    Zurück zur Liste
                                </Button>
                            </Col>
                        </Row>
                        <Row>
                            <FaCloudDownloadAlt className="text-energo animate__animated animate__tada" size={60}/>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <h4>Ihre Dateien</h4>
                                <p className="text-muted">Sind bereit für download</p>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {downloadFiles !== null && downloadFiles.poolrechnungen !== undefined &&
                                    <p><a 
                                        href="/download/poolrechnungen" 
                                        onClick={e => downloadFileHandler(downloadFiles.poolrechnungen, downloadFiles.poolrechnungen_name, e)}  
                                        className="link-energo"
                                    >
                                        <FaFileZipper size={20} /> {downloadFiles.poolrechnungen_name}
                                    </a></p>
                                }
                                {downloadFiles !== null && downloadFiles.abrechnungen !== undefined &&
                                    <p><a 
                                        href="/download/abrechnungen" 
                                        onClick={e => downloadFileHandler(downloadFiles.abrechnungen, downloadFiles.abrechnungen_name, e)}
                                        className="link-energo"
                                    >
                                        <FaFileZipper size={20} /> {downloadFiles.abrechnungen_name}
                                    </a></p>
                                }
                                {downloadFiles !== null && downloadFiles.vorschreibungen !== undefined &&
                                    <p><a 
                                        href="/download/vorschreibungen" 
                                        onClick={e => downloadFileHandler(downloadFiles.vorschreibungen, downloadFiles.vorschreibungen_name, e)}
                                        className="link-energo"
                                    >
                                        <FaFileZipper size={20} /> {downloadFiles.vorschreibungen_name}
                                    </a></p>
                                }
                            </Col>
                        </Row>
                    </CardBody> :

                view === "error"?
                    <CardBody className="text-center">
                        <Row className="mt-3">
                            <FaFileCircleXmark size={60} className="text-danger" />
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <h4 className="text-danger">Fehler aufgetreten</h4>
                                <p className="text-muted">{downloadError}</p>
                            </Col>
                        </Row>
                    </CardBody>
                :null
            }
        </Card>
    )
}

export default DownloadInvoiceList;